<template>
  <div class="my-pulses-container">
    <div class="pulses-header">
      <h1>My Pulses</h1>
      <p class="subtitle">Share your thoughts and updates</p>
    </div>

    <div class="pulses-main-content">
      <!-- New Pulse Form - Left Column -->
      <div class="new-pulse-sidebar">
        <div class="new-pulse-card">
          <h3 class="sidebar-title">{{ editingPulse ? 'Edit Pulse' : 'New Pulse' }}</h3>
          <el-form :model="newPulse" ref="newPulseForm">
            <el-form-item>
              <el-input
                v-model="newPulse.content"
                type="textarea"
                :rows="6"
                placeholder="What's happening?"
                maxlength="1500"
                show-word-limit
              />
            </el-form-item>

            <!-- Media Upload (optional) -->
            <el-form-item>
              <el-upload
                class="pulse-uploader"
                action="#"
                :auto-upload="false"
                :on-change="handleFileChange"
                :on-remove="handleFileRemove"
                :file-list="newPulse.fileList"
                list-type="picture-card"
                :limit="4"
                accept="image/*"
              >
                <i class="el-icon-plus"></i>
              </el-upload>
              <div class="upload-tip">Upload up to 4 images (auto-resized to 1200px)</div>
            </el-form-item>

            <!-- Tags -->
            <el-form-item>
              <el-tag
                v-for="(tag, index) in newPulse.tags"
                :key="index"
                closable
                :disable-transitions="false"
                @close="handleTagClose(tag)"
                size="small"
                class="pulse-tag"
              >
                {{ tag }}
              </el-tag>
              <el-input
                v-if="tagInputVisible"
                v-model="tagInputValue"
                ref="tagInput"
                size="small"
                class="tag-input"
                @keyup.enter.native.prevent="handleTagInputConfirm"
                @blur="handleTagInputConfirm"
              />
              <el-button
                v-else
                size="small"
                @click="showTagInput"
                class="button-new-tag"
              >
                + Tag
              </el-button>
            </el-form-item>

            <!-- Custom Display Date (Optional) -->
            <el-form-item>
              <div class="custom-date-section">
                <el-checkbox v-model="useCustomDate" size="small">
                  Set custom display date
                </el-checkbox>
                <el-date-picker
                  v-if="useCustomDate"
                  v-model="newPulse.customDate"
                  type="datetime"
                  placeholder="Select date and time"
                  size="small"
                  format="yyyy-MM-dd HH:mm"
                  value-format="timestamp"
                  style="width: 100%; margin-top: 8px;"
                />
              </div>
            </el-form-item>

            <div class="form-actions">
              <el-button
                type="success"
                @click="polishWithAI"
                :loading="polishingWithAI"
                :disabled="!newPulse.content.trim()"
                icon="el-icon-magic-stick"
                size="small"
                block
              >
                AI Polish
              </el-button>
              <el-button
                type="primary"
                @click="submitPulse"
                :loading="submitting"
                icon="el-icon-s-promotion"
                size="small"
                block
              >
                {{ editingPulse ? 'Update' : 'Post' }}
              </el-button>
              <el-button v-if="editingPulse" @click="cancelEdit" size="small" block>Cancel</el-button>
            </div>
          </el-form>
        </div>
      </div>

      <!-- Pulses List - Right Column -->
      <div class="pulses-list">
      <el-skeleton :rows="3" animated v-if="loading" />

      <div v-else-if="pulses.length === 0" class="empty-state">
        <i class="el-icon-chat-line-square" style="font-size: 64px; color: #c0c4cc;"></i>
        <p>No pulses yet. Create your first pulse!</p>
      </div>

      <div v-else class="pulse-items">
        <div
          v-for="pulse in sortedPulses"
          :key="pulse.id"
          class="pulse-item"
        >
          <div class="pulse-header">
            <div class="pulse-author">
              <el-avatar
                :size="40"
                :src="userInfo.profile_picture"
                class="author-avatar"
              >
                {{ getUserInitial() }}
              </el-avatar>
              <div class="author-info">
                <div class="author-name">{{ getUserDisplayName() }}</div>
                <div class="pulse-meta">
                  <span class="pulse-date">{{ formatDate(pulse.display_date || pulse.updated_at) }}</span>
                  <span v-if="pulse.updated_at && pulse.updated_at !== pulse.created_at" class="pulse-edited">
                    · edited
                  </span>
                </div>
              </div>
            </div>
            <el-dropdown trigger="click" @command="handlePulseAction">
              <span class="pulse-menu-icon">
                <i class="el-icon-more"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :command="{action: 'edit', pulse}">Edit</el-dropdown-item>
                <el-dropdown-item :command="{action: 'delete', pulse}" divided>Delete</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>

          <div class="pulse-content" v-html="renderMarkdown(pulse.content)"></div>

          <!-- Images -->
          <div v-if="pulse.images && pulse.images.length > 0" class="pulse-images">
            <el-image
              v-for="(image, index) in pulse.images"
              :key="index"
              :src="getOptimizedImageUrl(image, 400)"
              :preview-src-list="pulse.images.map(img => getOptimizedImageUrl(img, 1200))"
              fit="cover"
              class="pulse-image"
            />
          </div>

          <!-- Tags -->
          <div v-if="pulse.tags && pulse.tags.length > 0" class="pulse-tags">
            <el-tag
              v-for="(tag, index) in pulse.tags"
              :key="index"
              size="mini"
              type="info"
            >
              {{ tag }}
            </el-tag>
          </div>

          <!-- Stats -->
          <div class="pulse-stats">
            <span class="stat-item">
              <i class="el-icon-view"></i>
              {{ pulse.views_count || 0 }}
            </span>
            <span class="stat-item">
              <i class="el-icon-chat-dot-round"></i>
              {{ pulse.comments_count || 0 }}
            </span>
            <span class="stat-item">
              <i class="el-icon-star-off"></i>
              {{ pulse.likes_count || 0 }}
            </span>
          </div>
        </div>
      </div>

      <!-- Load More -->
      <div v-if="hasMore && !loading" class="load-more">
        <el-button @click="loadMore" :loading="loadingMore">
          Load More
        </el-button>
      </div>
      </div>
    </div>

    <!-- AI Polish Preview Dialog -->
    <el-dialog
      title="AI Polished Content Preview"
      :visible.sync="polishDialogVisible"
      width="700px"
      :close-on-click-modal="false"
      custom-class="polish-preview-dialog"
    >
      <div class="polish-preview">
        <div class="preview-section">
          <h4>Polished Content</h4>
          <p class="preview-hint">You can edit the AI-polished content below before applying it:</p>
          <el-input
            v-model="polishedPulse.content"
            type="textarea"
            :rows="10"
            placeholder="Edit polished content..."
            class="polished-content-editor"
          />
          <div class="markdown-preview">
            <div class="preview-label">Preview:</div>
            <div class="polished-content" v-html="renderMarkdown(polishedPulse.content)"></div>
          </div>
        </div>

        <div class="preview-section">
          <h4>Tags</h4>
          <p class="preview-hint">Edit tags as needed:</p>
          <div class="tags-editor">
            <el-tag
              v-for="(tag, index) in polishedPulse.tags"
              :key="index"
              closable
              size="small"
              class="preview-tag"
              @close="removePolishedTag(index)"
            >
              {{ tag }}
            </el-tag>
            <el-input
              v-if="polishTagInputVisible"
              v-model="polishTagInputValue"
              ref="polishTagInput"
              size="small"
              class="tag-input"
              @keyup.enter.native.prevent="handlePolishTagInputConfirm"
              @blur="handlePolishTagInputConfirm"
            />
            <el-button
              v-else
              size="small"
              @click="showPolishTagInput"
              class="button-new-tag"
            >
              + Add Tag
            </el-button>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="polishDialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="applyPolishedContent">Confirm & Apply</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import * as qiniu from 'qiniu-js'

export default {
  name: 'MyPulses',
  data() {
    return {
      pulses: [],
      loading: false,
      loadingMore: false,
      submitting: false,
      hasMore: false,
      skip: 0,
      limit: 20,
      newPulse: {
        content: '',
        tags: [],
        fileList: [],
        images: [],
        customDate: null
      },
      editingPulse: null,
      tagInputVisible: false,
      tagInputValue: '',
      useCustomDate: false,
      polishingWithAI: false,
      polishDialogVisible: false,
      polishedPulse: {
        content: '',
        tags: []
      },
      polishTagInputVisible: false,
      polishTagInputValue: ''
    }
  },
  computed: {
    userInfo() {
      return this.$store.state.user_info || {}
    },
    sortedPulses() {
      return [...this.pulses].sort((a, b) => {
        const dateA = a.display_date || a.updated_at
        const dateB = b.display_date || b.updated_at
        return dateB - dateA // Descending order (newest first)
      })
    }
  },
  mounted() {
    this.fetchPulses()
  },
  methods: {
    async fetchPulses() {
      this.loading = true
      try {
        const response = await postRequest('getUserPulses', {
          user_id: this.$store.state.user_id,
          limit: this.limit,
          skip: this.skip
        })

        if (response.data.code === 0) {
          this.pulses = response.data.data || []
          this.hasMore = this.pulses.length < (response.data.total || 0)
        } else {
          this.$message.error(response.data.msg || 'Failed to load pulses')
        }
      } catch (error) {
        console.error('Error fetching pulses:', error)
        this.$message.error('Failed to load pulses')
      } finally {
        this.loading = false
      }
    },

    async loadMore() {
      this.loadingMore = true
      this.skip += this.limit
      try {
        const response = await postRequest('getUserPulses', {
          user_id: this.$store.state.user_id,
          limit: this.limit,
          skip: this.skip
        })

        if (response.data.code === 0) {
          const newPulses = response.data.data || []
          this.pulses = [...this.pulses, ...newPulses]
          this.hasMore = this.pulses.length < (response.data.total || 0)
        }
      } catch (error) {
        console.error('Error loading more pulses:', error)
      } finally {
        this.loadingMore = false
      }
    },

    async submitPulse() {
      if (!this.newPulse.content.trim()) {
        this.$message.warning('Please enter some content')
        return
      }

      this.submitting = true
      try {
        const pulseData = {
          content: this.newPulse.content,
          tags: this.newPulse.tags,
          images: this.newPulse.images
        }

        // Add custom display date if set
        if (this.useCustomDate && this.newPulse.customDate) {
          pulseData.display_date = Math.floor(this.newPulse.customDate / 1000) // Convert to seconds
        }

        if (this.editingPulse) {
          pulseData.id = this.editingPulse.id
        }

        const response = await postRequest('updateUserPulse', {
          user_id: this.$store.state.user_id,
          pulse_data: pulseData,
          is_edit: !!this.editingPulse
        })

        if (response.data.code === 0) {
          this.$message.success(this.editingPulse ? 'Pulse updated!' : 'Pulse posted!')
          this.resetForm()
          this.skip = 0
          await this.fetchPulses()
        } else {
          this.$message.error(response.data.msg || 'Failed to post pulse')
        }
      } catch (error) {
        console.error('Error submitting pulse:', error)
        this.$message.error('Failed to post pulse')
      } finally {
        this.submitting = false
      }
    },

    handlePulseAction({ action, pulse }) {
      if (action === 'edit') {
        this.editPulse(pulse)
      } else if (action === 'delete') {
        this.deletePulse(pulse)
      }
    },

    editPulse(pulse) {
      this.editingPulse = pulse
      this.newPulse.content = pulse.content
      this.newPulse.tags = [...(pulse.tags || [])]
      this.newPulse.images = [...(pulse.images || [])]

      // Populate custom date if it exists
      if (pulse.display_date) {
        this.useCustomDate = true
        this.newPulse.customDate = pulse.display_date * 1000 // Convert to milliseconds
      }

      // Populate fileList with existing images
      this.newPulse.fileList = (pulse.images || []).map((url, index) => ({
        uid: -index - 1,
        name: `image-${index}.jpg`,
        status: 'success',
        url: url
      }))

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    cancelEdit() {
      this.editingPulse = null
      this.resetForm()
    },

    deletePulse(pulse) {
      this.$confirm('Are you sure you want to delete this pulse?', 'Confirm Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await postRequest('deleteUserPulse', {
            user_id: this.$store.state.user_id,
            pulse_id: pulse.id
          })

          if (response.data.code === 0) {
            this.$message.success('Pulse deleted')
            this.pulses = this.pulses.filter(p => p.id !== pulse.id)
          } else {
            this.$message.error(response.data.msg || 'Failed to delete pulse')
          }
        } catch (error) {
          console.error('Error deleting pulse:', error)
          this.$message.error('Failed to delete pulse')
        }
      }).catch(() => {
        // User cancelled
      })
    },

    resetForm() {
      this.newPulse = {
        content: '',
        tags: [],
        fileList: [],
        images: [],
        customDate: null
      }
      this.useCustomDate = false
      this.editingPulse = null
    },

    async handleFileChange(file, fileList) {
      // Validate file
      const rawFile = file.raw
      if (!rawFile) return

      const isImage = rawFile.type.startsWith('image/')

      if (!isImage) {
        this.$message.error('Only image files are allowed!')
        // Remove invalid file from list
        const index = fileList.indexOf(file)
        if (index > -1) {
          fileList.splice(index, 1)
        }
        return
      }

      // Update fileList to show loading state
      this.newPulse.fileList = fileList

      try {
        // Resize image before upload
        const resizedBlob = await this.resizeImage(rawFile)

        // Convert blob to file
        const resizedFile = new File([resizedBlob], rawFile.name, {
          type: rawFile.type,
          lastModified: Date.now()
        })

        console.log('Original size:', (rawFile.size / 1024 / 1024).toFixed(2) + 'MB')
        console.log('Resized size:', (resizedFile.size / 1024 / 1024).toFixed(2) + 'MB')

        // Calculate file hash (MD5) for filename
        const fileHash = await this.calculateFileHash(resizedFile)

        // Get file extension
        const fileExt = rawFile.name.split('.').pop().toLowerCase()

        // Use hash as filename with extension
        const hashFilename = `${fileHash}.${fileExt}`

        console.log('File hash:', fileHash)
        console.log('Upload filename:', hashFilename)

        // Get upload token from backend
        const tokenResponse = await postRequest('getSingaporeQiniuToken', {})
        const token = tokenResponse.data.data

        // Upload to Qiniu with hash-based filename
        const observable = qiniu.upload(
          resizedFile,
          hashFilename,
          token
        )

        observable.subscribe({
          next: (result) => {
            // Upload progress
            file.percentage = Math.floor(result.total.percent)
          },
          error: (err) => {
            console.error('Upload error:', err)
            this.$message.error('Failed to upload image')
            // Remove failed file from fileList
            const index = fileList.indexOf(file)
            if (index > -1) {
              fileList.splice(index, 1)
              this.newPulse.fileList = [...fileList]
            }
          },
          complete: (res) => {
            // Upload complete - construct URL
            const imageUrl = 'https://ap.knowit.today/' + res.key

            // Add URL to images array
            this.newPulse.images.push(imageUrl)

            // Update file object with URL
            file.url = imageUrl
            file.status = 'success'

            this.$message.success('Image uploaded successfully')
            console.log('Uploaded image URL:', imageUrl)
          }
        })
      } catch (error) {
        console.error('Error processing image:', error)
        this.$message.error('Failed to process image')
        // Remove failed file from fileList
        const index = fileList.indexOf(file)
        if (index > -1) {
          fileList.splice(index, 1)
          this.newPulse.fileList = [...fileList]
        }
      }
    },

    resizeImage(file, maxWidth = 1200, maxHeight = 1200, quality = 0.85) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = (e) => {
          const img = new Image()

          img.onload = () => {
            // Calculate new dimensions while maintaining aspect ratio
            let width = img.width
            let height = img.height

            // Only resize if image is larger than max dimensions
            if (width <= maxWidth && height <= maxHeight) {
              // Image is already small enough, return original
              resolve(file)
              return
            }

            // Calculate scaling factor
            const scaleFactor = Math.min(maxWidth / width, maxHeight / height)
            width = Math.floor(width * scaleFactor)
            height = Math.floor(height * scaleFactor)

            // Create canvas
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height

            // Draw resized image
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, width, height)

            // Convert to blob
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  resolve(blob)
                } else {
                  reject(new Error('Failed to create blob'))
                }
              },
              file.type,
              quality
            )
          }

          img.onerror = () => {
            reject(new Error('Failed to load image'))
          }

          img.src = e.target.result
        }

        reader.onerror = () => {
          reject(new Error('Failed to read file'))
        }

        reader.readAsDataURL(file)
      })
    },

    calculateFileHash(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = async (e) => {
          try {
            const arrayBuffer = e.target.result
            const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
            const hashArray = Array.from(new Uint8Array(hashBuffer))
            const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
            resolve(hashHex)
          } catch (error) {
            reject(error)
          }
        }

        reader.onerror = () => {
          reject(new Error('Failed to read file for hashing'))
        }

        reader.readAsArrayBuffer(file)
      })
    },

    handleFileRemove(file, fileList) {
      // Remove the corresponding URL from images array
      if (file.url && this.newPulse.images.includes(file.url)) {
        const index = this.newPulse.images.indexOf(file.url)
        this.newPulse.images.splice(index, 1)
      }
      // Update fileList
      this.newPulse.fileList = fileList
    },

    handleTagClose(tag) {
      this.newPulse.tags.splice(this.newPulse.tags.indexOf(tag), 1)
    },

    showTagInput() {
      this.tagInputVisible = true
      this.$nextTick(() => {
        this.$refs.tagInput.$refs.input.focus()
      })
    },

    handleTagInputConfirm() {
      const inputValue = this.tagInputValue.trim()
      if (inputValue && !this.newPulse.tags.includes(inputValue)) {
        this.newPulse.tags.push(inputValue)
      }
      this.tagInputVisible = false
      this.tagInputValue = ''
    },

    formatDate(timestamp) {
      const date = timestamp < 10000000000 ? new Date(timestamp * 1000) : new Date(timestamp)
      const now = new Date()
      const diff = now - date
      const seconds = Math.floor(diff / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)

      if (days > 7) {
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      } else if (days > 0) {
        return `${days}d ago`
      } else if (hours > 0) {
        return `${hours}h ago`
      } else if (minutes > 0) {
        return `${minutes}m ago`
      } else {
        return 'Just now'
      }
    },

    renderMarkdown(content) {
      if (!content) return ''

      // Configure marked for better rendering
      marked.setOptions({
        breaks: true, // Enable line breaks
        gfm: true, // GitHub Flavored Markdown
        headerIds: false,
        mangle: false
      })

      const rawHtml = marked(content)
      return DOMPurify.sanitize(rawHtml, {
        ADD_TAGS: ['mark'], // Allow highlight tags
        ADD_ATTR: ['target', 'rel'] // Allow link attributes
      })
    },

    getUserDisplayName() {
      const firstName = this.userInfo.first_name || ''
      const lastName = this.userInfo.last_name || ''
      if (firstName && lastName) {
        return `${firstName} ${lastName}`
      }
      return this.userInfo.nick_name || this.userInfo.user_name || 'User'
    },

    getUserInitial() {
      const firstName = this.userInfo.first_name || ''
      if (firstName) {
        return firstName.charAt(0).toUpperCase()
      }
      const name = this.userInfo.nick_name || this.userInfo.user_name || 'U'
      return name.charAt(0).toUpperCase()
    },

    getOptimizedImageUrl(url, width = 800) {
      // Add Qiniu imageView2 parameters for optimized display
      // Mode 2: limit longest edge, keep aspect ratio
      // w: max width
      // q: quality (85%)
      if (!url) return ''
      if (url.includes('?')) return url // Already has parameters
      return `${url}?imageView2/2/w/${width}/q/85`
    },

    async polishWithAI() {
      if (!this.newPulse.content.trim()) {
        this.$message.warning('Please enter some content to polish')
        return
      }

      this.polishingWithAI = true

      // Show step 1 notification
      const step1Notification = this.$message({
        message: '✨ Step 1/2: AI is polishing your content...',
        type: 'info',
        duration: 0, // Don't auto-close
        iconClass: 'el-icon-loading'
      })

      try {
        // Fetch full user profile for better context
        let userProfile = {}
        try {
          const profileResponse = await postRequest('getUserProfile', {
            user_id: this.$store.state.user_id
          })
          if (profileResponse.data.code === 0) {
            userProfile = profileResponse.data.data
          }
        } catch (error) {
          console.warn('Failed to fetch user profile, using basic info:', error)
        }

        // Build comprehensive brand context
        const brandName = userProfile.user_nickName || this.getUserDisplayName()
        const brandType = userProfile.user_type || 'individual' // 'individual' or 'institution'
        const brandSignature = userProfile.user_signature || ''
        const brandIntro = userProfile.user_intro || ''

        let brandContext = `Brand/User: ${brandName}`
        brandContext += `\nType: ${brandType === 'institution' ? 'Institution/Organization' : 'Individual'}`
        if (brandSignature) {
          brandContext += `\nTagline: ${brandSignature}`
        }
        if (brandIntro) {
          brandContext += `\nAbout: ${brandIntro}`
        }

        // STEP 1: Polish the content
        const perspectiveGuidelines = brandType === 'institution'
          ? `PERSPECTIVE RULES FOR INSTITUTION/ORGANIZATION:
- Use FIRST-PERSON PLURAL: "we", "our", "us" (representing the organization)
- You MAY use the brand name "${brandName}" directly as a first-person subject (e.g., "${brandName} is excited to announce...")
- NEVER use third-person pronouns like "they", "it", "the company", "the organization"
- NEVER write as if you are an outsider documenting their news
- Think of it as: WE are ${brandName}, and WE are making this announcement

Examples for Institution:
❌ WRONG: "The company has launched..." (third-person)
❌ WRONG: "They are excited to announce..." (third-person)
❌ WRONG: "It has been a great year..." (third-person)
✅ CORRECT: "We've launched..." or "We're excited to announce..."
✅ CORRECT: "${brandName} is thrilled to announce..." (brand as first-person subject)
✅ CORRECT: "Our team has achieved..." or "We've achieved..."

Tone: More objective, professional, but still warm and engaging. Represents collective voice.`
          : `PERSPECTIVE RULES FOR INDIVIDUAL:
- Use FIRST-PERSON SINGULAR: "I", "me", "my" (personal voice)
- NEVER convert to third-person: "he", "she", "${brandName} is..."
- NEVER write as if you are a journalist documenting someone else's news
- Keep it personal, subjective, and authentic
- This is YOU talking about YOURSELF

Examples for Individual:
❌ WRONG: "${brandName} is moving to Singapore..." (third-person)
❌ WRONG: "He/She has achieved..." (third-person)
✅ CORRECT: "I'm moving to Singapore..."
✅ CORRECT: "I've achieved..." or "I'm excited to share..."

Tone: More subjective, personal, conversational yet professional.`

        const polishSystemPrompt = `You are a professional PR content editor helping ${brandType === 'institution' ? 'an organization' : 'someone'} polish their announcement/update for social media.

Your responsibilities:
1. Fix all typos, grammar defects, and make expressions more clear and professional
2. Polish the content to be engaging and newsworthy:
   - Reflects excitement and reveals the moment
   - Engages the audience and arouses interest
   - Maintains a professional yet approachable tone
   - Aligns with the brand's voice and positioning
3. Style the content in Markdown:
   - Add a title if needed using Markdown heading markers (## or ###)
   - Highlight important words or phrases in **bold**
   - Add appropriate emojis to enhance engagement (but don't overuse them - 1-3 max)
   - Use bullet points or numbered lists where appropriate

Brand Context:
${brandContext}

${perspectiveGuidelines}

CRITICAL: This is a FIRST-PERSON self-broadcast announcement, NOT third-person journalism.

IMPORTANT RESTRICTIONS:
- Do NOT add hashtags or tags in the content (tags are generated separately)
- Do NOT include "#tag" format anywhere in the polished content
- Focus ONLY on polishing the written content itself

OUTPUT FORMAT - STRICTLY FOLLOW:
- Return ONLY the polished markdown content itself
- Do NOT wrap it in code blocks (no \`\`\`markdown or \`\`\`)
- Do NOT add any prefixes like "Here is..." or "Here's the polished content:"
- Do NOT add any explanations, comments, or metadata
- Do NOT include JSON structure
- Do NOT add hashtags or tags
- Just return the raw markdown text that can be directly used

Example of CORRECT output format:
## Exciting News!
I'm thrilled to announce...

Example of WRONG output format:
\`\`\`markdown
## Exciting News!
...
\`\`\`

Or: "Here is the polished content: ..."

IMPORTANT: Return the pure markdown content directly, nothing else.`

        // Step 1: Call AI to polish content
        const polishPrompt = `${polishSystemPrompt}\n\nPlease polish this pulse content:\n\n${this.newPulse.content}`

        const polishResponse = await postRequest('exeModelGeneral', {
          content: polishPrompt,
          model: 'siliconflow/deepseek-ai/DeepSeek-V3'
        })

        if (!polishResponse || polishResponse.data.code !== 0 || !polishResponse.data.data) {
          step1Notification.close()
          this.$message.error('Failed to polish content')
          return
        }

        let polishedContent = polishResponse.data.data.trim()

        // Clean up any markdown code block wrappers if AI ignored instructions
        if (polishedContent.startsWith('```markdown')) {
          polishedContent = polishedContent.replace(/^```markdown\n?/g, '').replace(/```\n?$/g, '').trim()
        } else if (polishedContent.startsWith('```')) {
          polishedContent = polishedContent.replace(/^```\n?/g, '').replace(/```\n?$/g, '').trim()
        }

        // Remove any prefixes like "Here is the polished content:"
        const prefixPatterns = [
          /^Here is the polished content:?\s*/i,
          /^Here's the polished content:?\s*/i,
          /^Polished content:?\s*/i,
          /^Here is:?\s*/i,
          /^Here's:?\s*/i
        ]
        prefixPatterns.forEach(pattern => {
          polishedContent = polishedContent.replace(pattern, '')
        })

        polishedContent = polishedContent.trim()

        // Close step 1 notification and show step 2
        step1Notification.close()
        this.$message.success('✓ Step 1/2: Content polished successfully!')

        const step2Notification = this.$message({
          message: '🏷️ Step 2/2: AI is generating relevant tags...',
          type: 'info',
          duration: 0, // Don't auto-close
          iconClass: 'el-icon-loading'
        })

        // STEP 2: Generate relevant tags based on the polished content
        const tagSystemPrompt = `You are a social media expert specializing in content tagging and categorization.

Analyze the given content and suggest relevant, engaging tags that will help the post reach the right audience.

Guidelines:
- Suggest exactly 3 relevant tags
- Tags should be concise (1-2 words each)
- Mix of broad and specific tags
- Consider trending topics if relevant
- Use proper capitalization (e.g., "AI", "Web Development", "Marketing")

IMPORTANT: Return ONLY a JSON array of exactly 3 tag strings. Example format:
["Tag1", "Tag2", "Tag3"]

Do NOT include any explanations or additional text.`

        // Step 2: Call AI to generate tags
        const tagPrompt = `${tagSystemPrompt}\n\nGenerate relevant tags for this content:\n\n${polishedContent}\n\nExisting tags: ${this.newPulse.tags.join(', ') || 'none'}`

        const tagResponse = await postRequest('exeModelGeneral', {
          content: tagPrompt,
          model: 'siliconflow/deepseek-ai/DeepSeek-V3'
        })

        const tagResponseData = (tagResponse && tagResponse.data.code === 0 && tagResponse.data.data)
          ? tagResponse.data.data.trim()
          : ''

        let suggestedTags = []
        try {
          // Try to parse as JSON array
          let jsonString = tagResponseData
          if (jsonString.startsWith('```json')) {
            jsonString = jsonString.replace(/```json\n?/g, '').replace(/```\n?$/g, '')
          } else if (jsonString.startsWith('```')) {
            jsonString = jsonString.replace(/```\n?/g, '').replace(/```\n?$/g, '')
          }
          suggestedTags = JSON.parse(jsonString)

          // Ensure it's an array
          if (!Array.isArray(suggestedTags)) {
            suggestedTags = []
          }
        } catch (e) {
          console.error('Failed to parse tags response:', e)
          // Keep existing tags if parsing fails
          suggestedTags = [...this.newPulse.tags]
        }

        // Merge existing tags with suggested tags (avoid duplicates)
        const existingTagsLower = this.newPulse.tags.map(t => t.toLowerCase())
        const uniqueNewTags = suggestedTags.filter(tag =>
          !existingTagsLower.includes(tag.toLowerCase())
        )
        const finalTags = [...this.newPulse.tags, ...uniqueNewTags]

        // Close step 2 notification
        step2Notification.close()
        this.$message.success('✓ Step 2/2: Tags generated successfully!')

        // Set polished content and tags
        this.polishedPulse = {
          content: polishedContent,
          tags: finalTags
        }

        // Show final success message
        this.$message({
          message: '🎉 AI Polish completed! Review the results below.',
          type: 'success',
          duration: 3000
        })

        // Show preview dialog
        this.polishDialogVisible = true
      } catch (error) {
        console.error('Error polishing content:', error)

        // Close any open notifications
        if (step1Notification) step1Notification.close()
        if (typeof step2Notification !== 'undefined') step2Notification.close()

        this.$message.error('❌ Failed to polish content with AI. Please try again.')
      } finally {
        this.polishingWithAI = false
      }
    },

    applyPolishedContent() {
      // Apply the polished content to the main form
      this.newPulse.content = this.polishedPulse.content

      // Replace tags entirely with the edited polished tags
      this.newPulse.tags = [...this.polishedPulse.tags]

      // Close dialog
      this.polishDialogVisible = false

      this.$message.success('Polished content applied!')
    },

    removePolishedTag(index) {
      this.polishedPulse.tags.splice(index, 1)
    },

    showPolishTagInput() {
      this.polishTagInputVisible = true
      this.$nextTick(() => {
        this.$refs.polishTagInput.$refs.input.focus()
      })
    },

    handlePolishTagInputConfirm() {
      const inputValue = this.polishTagInputValue.trim()
      if (inputValue && !this.polishedPulse.tags.includes(inputValue)) {
        this.polishedPulse.tags.push(inputValue)
      }
      this.polishTagInputVisible = false
      this.polishTagInputValue = ''
    }
  }
}
</script>

<style scoped>
.my-pulses-container {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 40px 20px;
}

.pulses-header {
  margin-bottom: 32px;
  text-align: center;
}

.pulses-header h1 {
  font-size: 32px;
  font-weight: 700;
  color: #303133;
  margin: 0 0 8px 0;
}

.subtitle {
  font-size: 16px;
  color: #909399;
  margin: 0;
}

.pulses-main-content {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.new-pulse-sidebar {
  width: 320px;
  flex-shrink: 0;
}

.new-pulse-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 20px;
}

.sidebar-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 16px 0;
}

.form-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pulse-tag {
  margin-right: 8px;
  margin-bottom: 8px;
}

.tag-input {
  width: 100px;
  margin-right: 8px;
}

.button-new-tag {
  height: 24px;
  line-height: 22px;
  padding: 0 12px;
}

.pulses-list {
  flex: 1;
  min-width: 0;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #909399;
}

.empty-state p {
  margin-top: 16px;
  font-size: 16px;
}

.pulse-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pulse-item {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s;
}

.pulse-item:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.pulse-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.pulse-author {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.author-avatar {
  flex-shrink: 0;
}

.author-info {
  flex: 1;
  min-width: 0;
}

.author-name {
  font-weight: 600;
  font-size: 15px;
  color: #303133;
  margin-bottom: 2px;
}

.pulse-meta {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pulse-date {
  font-size: 13px;
  color: #909399;
}

.pulse-edited {
  font-size: 12px;
  color: #c0c4cc;
  font-style: italic;
}

.pulse-menu-icon {
  cursor: pointer;
  color: #909399;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
  flex-shrink: 0;
}

.pulse-menu-icon:hover {
  background: #f5f7fa;
  color: #606266;
}

.pulse-content {
  font-size: 16px;
  line-height: 1.6;
  color: #303133;
  word-wrap: break-word;
  margin-bottom: 16px;
}

/* Markdown content styling */
.pulse-content :deep(h1),
.pulse-content :deep(h2),
.pulse-content :deep(h3),
.pulse-content :deep(h4),
.pulse-content :deep(h5),
.pulse-content :deep(h6) {
  margin-top: 16px;
  margin-bottom: 8px;
  font-weight: 600;
  line-height: 1.4;
  color: #1a1a1a;
}

.pulse-content :deep(h1) { font-size: 24px; }
.pulse-content :deep(h2) { font-size: 20px; }
.pulse-content :deep(h3) { font-size: 18px; }
.pulse-content :deep(h4) { font-size: 16px; }

.pulse-content :deep(h1:first-child),
.pulse-content :deep(h2:first-child),
.pulse-content :deep(h3:first-child) {
  margin-top: 0;
}

.pulse-content :deep(p) {
  margin: 0 0 8px 0;
}

.pulse-content :deep(p:last-child) {
  margin-bottom: 0;
}

.pulse-content :deep(strong) {
  font-weight: 600;
  color: #1a1a1a;
}

.pulse-content :deep(em) {
  font-style: italic;
}

.pulse-content :deep(a) {
  color: #409eff;
  text-decoration: none;
  transition: color 0.2s;
}

.pulse-content :deep(a:hover) {
  color: #66b1ff;
  text-decoration: underline;
}

.pulse-content :deep(ul),
.pulse-content :deep(ol) {
  margin: 8px 0;
  padding-left: 24px;
}

.pulse-content :deep(li) {
  margin: 4px 0;
}

.pulse-content :deep(blockquote) {
  margin: 8px 0;
  padding: 8px 12px;
  border-left: 3px solid #409eff;
  background-color: #f5f7fa;
  color: #606266;
}

.pulse-content :deep(code) {
  padding: 2px 6px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  background-color: #f0f2f5;
  color: #e6428b;
  border-radius: 3px;
}

.pulse-content :deep(pre) {
  margin: 8px 0;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
  overflow-x: auto;
}

.pulse-content :deep(pre code) {
  padding: 0;
  background-color: transparent;
  color: #303133;
}

.pulse-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 8px 0;
}

.pulse-content :deep(hr) {
  margin: 12px 0;
  border: none;
  border-top: 1px solid #e4e7ed;
}

.pulse-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
  margin-bottom: 16px;
}

.pulse-image {
  width: 100%;
  height: 150px;
  border-radius: 8px;
}

.pulse-tags {
  margin-bottom: 12px;
}

.pulse-tags .el-tag {
  margin-right: 8px;
}

.pulse-stats {
  display: flex;
  gap: 24px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #909399;
}

.stat-item i {
  font-size: 16px;
}

.load-more {
  text-align: center;
  margin-top: 24px;
}

.pulse-uploader :deep(.el-upload-list--picture-card .el-upload-list__item) {
  width: 80px;
  height: 80px;
}

.pulse-uploader :deep(.el-upload--picture-card) {
  width: 80px;
  height: 80px;
  line-height: 80px;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

.custom-date-section {
  padding: 12px;
  background: #f5f7fa;
  border-radius: 8px;
}

.custom-date-section .el-checkbox {
  font-size: 13px;
  color: #606266;
}

/* AI Polish Dialog Styles */
.polish-preview {
  padding: 12px 0;
}

.preview-section {
  margin-bottom: 32px;
}

.preview-section:last-child {
  margin-bottom: 0;
}

.preview-section h4 {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
}

.preview-hint {
  font-size: 13px;
  color: #909399;
  margin: 0 0 12px 0;
}

.polished-content-editor {
  margin-bottom: 16px;
}

.polished-content-editor :deep(.el-textarea__inner) {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.6;
}

.markdown-preview {
  margin-top: 16px;
}

.preview-label {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
}

.polished-content {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 16px;
  font-size: 15px;
  line-height: 1.6;
  color: #303133;
  word-wrap: break-word;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e4e7ed;
}

/* Markdown styling in preview */
.polished-content :deep(h1),
.polished-content :deep(h2),
.polished-content :deep(h3) {
  margin-top: 12px;
  margin-bottom: 8px;
  font-weight: 600;
  line-height: 1.4;
  color: #1a1a1a;
}

.polished-content :deep(h1) { font-size: 24px; }
.polished-content :deep(h2) { font-size: 20px; }
.polished-content :deep(h3) { font-size: 18px; }

.polished-content :deep(h1:first-child),
.polished-content :deep(h2:first-child),
.polished-content :deep(h3:first-child) {
  margin-top: 0;
}

.polished-content :deep(p) {
  margin: 0 0 8px 0;
}

.polished-content :deep(p:last-child) {
  margin-bottom: 0;
}

.polished-content :deep(strong) {
  font-weight: 600;
  color: #1a1a1a;
}

.polished-content :deep(ul),
.polished-content :deep(ol) {
  margin: 8px 0;
  padding-left: 24px;
}

.polished-content :deep(li) {
  margin: 4px 0;
}

.tags-editor {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.preview-tag {
  background: #ecf5ff;
  color: #409eff;
  border-color: #b3d8ff;
}

.polish-preview-dialog {
  max-height: 90vh;
  overflow-y: auto;
}

/* Responsive */
@media (max-width: 768px) {
  .my-pulses-container {
    padding: 12px;
  }

  .pulses-main-content {
    flex-direction: column;
  }

  .new-pulse-sidebar {
    width: 100%;
  }

  .new-pulse-card {
    padding: 16px;
    position: static;
  }

  .pulse-item {
    padding: 16px;
  }

  .pulses-header h1 {
    font-size: 24px;
  }
}
</style>
